import { useContext } from "react";

import { useQuery } from "@tanstack/react-query";

import { ViewRequest, getViewRequest, request } from "@/App/api/request";
import { AuthenticationContext, MemberEligibilityContext } from "@/App/context";

interface ListProvidersRequest {
  readonly npi: string;
  readonly name?: string;
  readonly first_name?: string;
  readonly last_name?: string;
  readonly phone_number?: string;
  readonly provider_business_practice_state?: string;
  readonly lexis_nexis_provider_id?: string;
  readonly npi_operator?: string;
  readonly entity_type_code?: string;
  readonly page_number?: string;
  readonly results_per_page?: string;
  readonly expected_fields?: string;
}

export interface Provider {
  readonly npi?: string;
  readonly provider_first_name?: string;
  readonly provider_last_name?: string;
  readonly provider_business_practice_address_line_1?: string;
  readonly provider_business_practice_address_line_2?: string;
  readonly provider_business_practice_city?: string;
  readonly provider_business_practice_state?: string;
  readonly provider_business_practice_zip_code?: string;
  readonly provider_business_practice_phone?: string;
}

interface ProviderListItem {
  readonly provider: Provider;
}

interface ListProvidersResponse {
  readonly results: ProviderListItem[];
  readonly result_count: string;
}

const listProviders = async (authToken: string, body: ListProvidersRequest & Partial<ViewRequest>) => {
  return request<typeof body, ListProvidersResponse>(
    {
      endpoint: "provider/list",
      body: { ...body },
    },
    authToken,
  );
};

export const useListProvidersQuery = (params: ListProvidersRequest, enabled = true) => {
  const { isViewingDataholder, memberEligibility } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);
  const queryKey = ["prescriberList", params.npi, params.phone_number, params.name];

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return listProviders(await getAuthToken(), {
          ...params,
          ...getViewRequest(memberEligibility),
        });
      } else {
        return listProviders(await getAuthToken(), params);
      }
    },
    queryKey: queryKey,
    enabled,
    retry: false,
  });

  return query;
};
